import { ChargePoint, ChargingLocation, DetailedChargePoint } from '../types';
import { BackendLocation, BackendChargePoint, EVSE } from '../types/location';

/**
 * Converts a basic ChargePoint to a detailed ChargePoint with connectors
 * Uses real EVSE data from the backend instead of creating dummy connectors
 */
export function toDetailedChargePoint(chargepoint: ChargePoint): DetailedChargePoint {
  // If we have real EVSE data, use it to create connectors
  if (chargepoint.evses && chargepoint.evses.length > 0) {
    return {
      id: chargepoint.id,
      name: chargepoint.name || `${chargepoint.type} Charger`,
      reportsPreparing: chargepoint.reportsPreparing,
      connectors: chargepoint.evses.map((evse, index) => ({
        id: evse.id,
        evseId: evse.id, // For real-time status mapping
        // Derive gun number from connectorId (parse as int) or use index + 1
        gunNumber: parseInt(evse.connectorId, 10) || (index + 1),
        type: evse.connectorType,
        power: `${evse.maxOutputPowerKw} kW`,
        status: evse.status,
        active: evse.isActive,
      })),
    };
  }

  // No EVSE data - return empty connectors array
  return {
    id: chargepoint.id,
    name: chargepoint.name || 'Charge Point',
    reportsPreparing: chargepoint.reportsPreparing,
    connectors: [], // Empty array instead of dummy connector
  };
}

/**
 * Formats duration from minutes to human-readable string
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
}

/**
 * Gets battery color gradient based on level
 */
export function getBatteryColorGradient(batteryLevel: number): string {
  if (batteryLevel < 20) return 'from-red-400 to-red-600';
  if (batteryLevel < 50) return 'from-orange-400 to-orange-600';
  if (batteryLevel < 80) return 'from-yellow-400 to-[#8bc43e]';
  return 'from-[#8bc43e] to-green-600';
}

/**
 * Backend Location Transformers
 * Convert backend API responses to UI types
 */

/**
 * Aggregates EVSE statuses to determine overall charge point status
 * Maps backend EVSE status values to frontend charge point status values
 * Priority: available > occupied > out_of_order
 */
export function getAggregatedStatus(evses: EVSE[]): 'available' | 'occupied' | 'out_of_order' | 'maintenance' {
  // Filter active EVSEs only
  const activeEvses = evses.filter(evse => evse.isActive);

  // If no active EVSEs, charge point is out of order
  if (activeEvses.length === 0) {
    return 'out_of_order';
  }

  // If any EVSE is available, charge point is available
  if (activeEvses.some(evse => evse.status === 'available')) {
    return 'available';
  }

  // If any EVSE is occupied (charging, preparing, reserved, suspended, finishing)
  const occupiedStatuses = ['charging', 'preparing', 'reserved', 'suspended_evse', 'suspended_ev', 'finishing', 'occupied'];
  if (activeEvses.some(evse => occupiedStatuses.includes(evse.status))) {
    return 'occupied';
  }

  // If any EVSE is in maintenance
  if (activeEvses.some(evse => evse.status === 'maintenance')) {
    return 'maintenance';
  }

  // If any EVSE is faulted or unavailable
  const faultedStatuses = ['faulted', 'unavailable', 'out_of_order'];
  if (activeEvses.some(evse => faultedStatuses.includes(evse.status))) {
    return 'out_of_order';
  }

  // Default fallback
  return 'out_of_order';
}

/**
 * Transforms backend ChargePoint to UI ChargePoint
 */
export function transformChargePointToUI(backendChargePoint: BackendChargePoint): ChargePoint {
  const activeEvses = backendChargePoint.evses.filter(evse => evse.isActive);

  // Get aggregated status
  const status = getAggregatedStatus(backendChargePoint.evses);

  // Get connector type from first active EVSE
  const connectorType = activeEvses[0]?.connectorType || 'Unknown';

  // Get max power from active EVSEs
  const maxPower = activeEvses.length > 0
    ? Math.max(...activeEvses.map(evse => evse.maxOutputPowerKw))
    : 0;

  return {
    id: backendChargePoint.id,
    name: backendChargePoint.chargerName,  // Map chargerName to name
    type: connectorType,
    stationType: backendChargePoint.stationType === 'dc' ? 'DC' : 'AC',
    power: `${maxPower} kW`,
    status,
    isOnline: backendChargePoint.isOnline,
    reportsPreparing: backendChargePoint.reportsPreparing,
    // Preserve EVSE data for detailed view
    evses: backendChargePoint.evses.map(evse => ({
      id: evse.id,
      connectorId: evse.connectorId,
      maxOutputPowerKw: evse.maxOutputPowerKw,
      connectorType: evse.connectorType,
      status: evse.status,
      isActive: evse.isActive,
    })),
  };
}

/**
 * Transforms backend Location to UI ChargingLocation
 */
export function transformLocationToChargingLocation(backendLocation: BackendLocation): ChargingLocation {
  // Extract cover photo (primary image or first image)
  const coverPhoto = backendLocation.images?.find(img => img.isPrimary)?.url
    || backendLocation.images?.[0]?.url;

  // Extract all photo URLs
  const photos = backendLocation.images?.map(img => img.url) || [];

  // Extract facility names
  const facilities = backendLocation.facilities?.map(f => f.facility) || [];

  // Transform charge points
  const chargepoints = (backendLocation.chargePoints || []).map(transformChargePointToUI);

  // Calculate availability (true if any chargepoint is available)
  const available = chargepoints.some(cp => cp.status === 'available');

  return {
    id: backendLocation.id,
    name: backendLocation.name,
    position: [backendLocation.latitude, backendLocation.longitude],
    available,
    distance: backendLocation.distance || 0,
    address: backendLocation.address,
    city: backendLocation.city,
    facilities,
    coverPhoto,
    photos,
    chargepoints,
    matchedField: (backendLocation as any).matchedField,
  };
}

/**
 * Transforms an array of backend locations to UI locations
 */
export function transformLocations(backendLocations: BackendLocation[]): ChargingLocation[] {
  return backendLocations.map(transformLocationToChargingLocation);
}
